import { z } from 'zod';

import { stripMarkup } from '../ai/sanitize.js';
import type { TopicSuggestion } from '../ai/types.js';

export const DEFAULT_CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000; // 1 day

/**
 * The well-known **default account** every topic falls back to (NM-20, phase 2a).
 *
 * Data is now isolated per account (`account_id` on topics; items/runs inherit it
 * through their topic). Phase 2a is deliberately **non-enforcing**: a request
 * with no valid session is served as this account rather than rejected, so the
 * existing single-tenant app keeps working while the isolation machinery lands.
 * The real account row registered against it reuses the `member-a` development
 * persona (see `src/auth/seed.ts`), so a signed-in `member-a` owns the data an
 * upgraded single-tenant database already held. Enforcement — requiring auth,
 * blocking suspended/disabled — is phase 2b.
 */
export const DEFAULT_ACCOUNT_ID = 'default-account';

/**
 * Cadence constants (NM-11, NM-127). Checking is automatic and not
 * user-configurable; since NM-127 the interval is driven by a subject's
 * subscriber count (`src/cadence.ts`), and these two constants are the values of
 * two of those tiers — `CHECK_INTERVAL_MS` (24h) the mid tier, and the
 * former-high-priority 6h the 21–99 tier. A sweep runs at most `CHECK_CONCURRENCY`
 * checks at once.
 *
 * Exported so both the server (`checks.ts`, `cadence.ts`) and the client read the
 * same numbers.
 */
export const CHECK_INTERVAL_MS = DEFAULT_CHECK_INTERVAL_MS;
export const HIGH_PRIORITY_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours (the 21–99-subscriber tier)
export const CHECK_CONCURRENCY = 3;

/**
 * Story-retention window, in days (NEWS-87). **Fixed at two years (NM-169)** —
 * it used to be a per-account setting (3 months … forever), but that was a knob
 * nobody needed to turn, so the control was removed and the window is now a
 * constant. Long enough that nobody hits it by surprise, short enough that the
 * data file stops growing without bound. `runs` is capped and images are pruned;
 * items were the one collection with no ceiling. **Bookmarked stories are never
 * pruned** regardless — see `pruneOldItems`.
 */
export const RETENTION_DAYS = 730;

/** A topic the user wants news about. */
export const TopicSchema = z.object({
  id: z.string(),
  /**
   * The canonical grouping key (`topicKey(name)` — trim/lowercase/collapse
   * whitespace), unique across the whole install (NM-126, docs/43). It is what
   * makes a subject *shared*: two accounts naming the same subject resolve to one
   * row, one check, one story set. Defaulted from `name` on read so a row from an
   * export or a recovered database that predates the column still parses.
   */
  canonicalKey: z.string().default(''),
  /**
   * The name's **base key** (`topicKey(name)`), shared by every sense-fork of a
   * name (NM-164, docs/47). Equal to `canonicalKey` for an ordinary subject; for a
   * sense-fork the two differ (the fork's canonical key carries a sense suffix).
   * Defaulted from `name` on read so a row that predates the column still parses,
   * and read by the client to group a name's forks for the re-add picker.
   */
  baseKey: z.string().default(''),
  name: z.string().min(1),
  /**
   * Whether the reader has paused this subject **in their own view** (NM-126).
   * A per-account fact now — it lives on the `subscriptions` row, not the shared
   * subject — populated here from the reader's subscription at query time so the
   * returned `Topic` shape is unchanged for the client. Pausing my subscription
   * hides the subject from my feed; it does not stop the shared check for other
   * subscribers. Defaults false (e.g. a subject read outside any subscription).
   */
  paused: z.boolean().default(false),
  /**
   * Whether this is a curated **catalog** subject (NM-135) rather than a
   * user-created custom one. Catalog subjects are shared and cheap per person, so
   * plan entitlements are generous with them; **custom** subjects (the default)
   * are what a plan's cap meters. Defaults false.
   */
  catalog: z.boolean().default(false),
  /**
   * The topic's selected **focus** angles (NM-27), by **id** from the fixed
   * vocabulary in `src/topic-focus.ts`. Replaces the free-text `guidance`
   * textarea with a multi-select of angle chips, compiled into prompt
   * instructions by `buildContextLines`.
   *
   * A plain `string[]`, deliberately **not** a zod enum — the same call
   * `category` makes just above. The vocabulary is edited in code, so an enum
   * would let a taxonomy edit condemn stored rows and take the whole load down.
   * An id no longer in the vocabulary is dropped at *use* time (the prompt
   * compiler and the sidebar both skip unknown ids), never rejected on read.
   * Writes are constrained to known ids at the API boundary instead.
   */
  focus: z.array(z.string()).default([]),
  createdAt: z.string(),
  /**
   * ISO timestamp of the last check *attempt*, success or failure; null if
   * never attempted. Drives scheduling — advancing it on failure is what stops
   * a broken provider from being retried every tick.
   */
  lastCheckedAt: z.string().nullable(),
  /**
   * ISO timestamp that news is *covered through* — the last check that
   * actually succeeded. This is what the prompt asks from, so a failed check
   * can't quietly swallow the pending window: fail at 09:00 with news pending
   * since five days ago and the next successful check still asks for all five
   * days. Null until the first success.
   */
  coveredThroughAt: z.string().nullable().default(null),
  /**
   * Section slug from the taxonomy in `src/categories.ts` (NEWS-97); null until
   * the topic has been classified.
   *
   * A plain string, deliberately **not** a zod enum (FR-22.3). The taxonomy is
   * edited in code, so an enum would let any edit invalidate stored rows and
   * take the whole load down with it. A slug that no longer resolves simply
   * renders as Uncategorized.
   */
  category: z.string().nullable().default(null),
  /** Second-level slug within `category`; null means no subcategory fits. */
  subcategory: z.string().nullable().default(null),
  /**
   * Who set the category. `manual` is a promise: automatic classification never
   * overwrites a choice the user made by hand (FR-22.7).
   */
  categorySource: z.enum(['auto', 'manual']).default('auto').catch('auto'),
  /**
   * What the classifier had said, on a topic a person then overruled (NEWS-404).
   *
   * Only ever written when a **manual** choice replaces an **auto** one, and
   * never cleared — it is a record of a disagreement, not current state. `null`
   * means no correction has been observed, which is different from "the
   * classifier was right": a topic nobody has looked at reads the same way.
   *
   * This exists because a wrong-but-reasonable section is otherwise invisible:
   * the pill looks plausible, no check fails, and nobody reports it. An override
   * is the one moment a user says, inside the app, that the classifier missed —
   * and the *pair* is what matters, since the NEWS-397 hypothesis is about
   * near-neighbours (Money↔Business, Media↔Culture) rather than raw miss count.
   */
  autoCategory: z.string().nullable().default(null),
  /**
   * Consecutive failed checks, reset to 0 by any success (NEWS-110). Drives the
   * length of `retryAfter`, so a provider that stays broken is retried less and
   * less often instead of every tick.
   */
  consecutiveFailures: z.number().int().nonnegative().default(0).catch(0),
  /**
   * ISO timestamp before which this topic must not be checked again (NEWS-110).
   *
   * Set after a retryable failure *instead of* advancing `lastCheckedAt`. The
   * two express different things: `lastCheckedAt` means "we have news up to
   * here", and moving it for a network outage claimed a check had happened that
   * hadn't — which is what made a five-minute outage cost a whole interval.
   */
  retryAfter: z.string().nullable().default(null),
  /**
   * ISO timestamp of the last time this topic's stories were **cleared**
   * (NEWS-291); null if they never have been.
   *
   * Clearing resets the topic to its initial state — `lastCheckedAt` and
   * `coveredThroughAt` go back to null, so every surface reads as never checked.
   * That alone would make the topic *due*, and a clear would start the sweep it
   * had just cancelled (NEWS-271). This field is what keeps the two apart:
   * display asks `lastCheckedAt`, scheduling asks the later of the two, so a
   * cleared topic reads as new **and** waits a full interval.
   *
   * Only consulted while `lastCheckedAt` is null — see `scheduleBaseline` in
   * `src/checks.ts`. Kept rather than wiped by the next check, because it is the
   * record of when the reset happened, and the undo needs the previous value.
   */
  clearedAt: z.string().nullable().default(null),
  /**
   * The AI-optimized Brave search query for this topic (NM-16), built once when
   * the topic is first checked (not per check) and reused every time after.
   *
   * Null means "not built yet" — the next check builds one and persists it here.
   * It is nulled again whenever the topic's `name` or `guidance` changes, since
   * either changes what a good query is, so the following check rebuilds it.
   */
  searchQuery: z.string().nullable().default(null),
  /**
   * Topic disambiguation (NM-163, docs/47). The candidate **senses** the
   * ambiguity pass produced for an ambiguous brand-new subject — short phrases
   * like "the rock band" / "the sleep pattern" for "R.E.M." Empty (the default)
   * means the subject is not ambiguous, or was never classified.
   *
   * A plain `string[]`, defaulted on read like `focus`, so a row from an export
   * or a database that predates the column still parses.
   */
  senseOptions: z.array(z.string()).default([]),
  /**
   * The sense a subscriber picked from {@link senseOptions} (NM-163); null until
   * then. A subject with senses but no chosen sense is *awaiting a sense*: gated
   * (no check runs) and shown as "needs attention". Once chosen, it feeds the
   * check prompt and the first check is allowed to run.
   */
  chosenSense: z.string().nullable().default(null),
});
export type Topic = z.infer<typeof TopicSchema>;

/**
 * Whether a subject is **awaiting a sense selection** (NM-163) — it was found
 * ambiguous at add time, so it has candidate senses but none chosen yet. While
 * this holds, its check is gated (a no-op) and the client shows a "needs
 * attention" sense picker. A shared helper so the server (the check gate, the
 * route) and the client agree on the one condition.
 */
export function isAwaitingSense(topic: Pick<Topic, 'senseOptions' | 'chosenSense'>): boolean {
  return topic.senseOptions.length > 0 && topic.chosenSense === null;
}

export const NewsSourceSchema = z.object({
  title: z.string().transform(stripMarkup),
  url: z.string(),
  /**
   * The outlet that published it (NEWS-82), e.g. "Reuters". Null when the model
   * didn't say; the UI falls back to the URL's registrable domain, which is
   * usually close enough to be useful and never wrong in a misleading way.
   */
  outlet: z.string().nullable().default(null),
  /**
   * When the article was published, as `YYYY-MM-DD` (NEWS-82). **Null is the
   * normal case** — the model often doesn't know, and inventing a date would be
   * worse than showing none, since recency is exactly what a reader is judging.
   *
   * Distinct from the item's `foundAt`: a catch-up check after a week's
   * downtime files week-old articles under today.
   */
  publishedAt: z.string().nullable().default(null),
  /**
   * The outlet's favicon, cached locally (NEWS-169). Shaped exactly like the
   * item's `image` — `hash` names the file the image route serves, `sourceUrl`
   * is kept for attribution and debugging and is never fetched by the browser.
   *
   * Per **source**, not per item, because a story can cite several outlets and
   * each link wears its own mark. Content-addressed by icon URL, so sources
   * sharing an origin share one cache entry.
   *
   * Null is an ordinary outcome — an outlet with no reachable icon, or a story
   * stored before this existed. The feed falls back to the arrow glyph it always
   * had, which is why this is safe to default rather than migrate.
   */
  favicon: z.object({ hash: z.string(), sourceUrl: z.string() }).nullable().default(null).catch(null),
});
export type NewsSource = z.infer<typeof NewsSourceSchema>;

/**
 * A news story found for a topic.
 *
 * Exported as the plain object so callers can reach one field's schema
 * (`NewsItemFieldsSchema.shape.title`, used by the prompt's title list).
 * `NewsItemSchema` wraps it in the `threadId` fallback below, which a `ZodObject`
 * cannot express on its own — the fallback needs a sibling field.
 */
export const NewsItemFieldsSchema = z.object({
  id: z.string(),
  topicId: z.string(),
  // Sanitized on read as well as on write: items stored before the parse-time
  // strip existed still carry citation markup, and this cleans them the next
  // time the data file is loaded rather than needing a migration.
  title: z.string().transform(stripMarkup),
  summary: z.string().transform(stripMarkup),
  /** Whether the user bookmarked this story (NEWS-42). Defaults false. */
  saved: z.boolean().default(false),
  /**
   * Whether the user flagged this story as off-topic (NEWS-61). Hidden from the
   * feed by default and fed to the topic's prompt as a negative example, so the
   * model can infer what the user actually meant. Defaults false.
   */
  offTopic: z.boolean().default(false),
  /**
   * Admin-moderated removal (NM-106). Hides the story from every user-facing read
   * like `offTopic`, but keeps the row (its dedupe key still blocks re-add) and
   * does NOT feed the off-topic negative-example list. Reversible. Defaults false.
   */
  removed: z.boolean().default(false),
  sources: z.array(NewsSourceSchema),
  /**
   * Lead image, cached locally. `hash` names the file the image route serves;
   * `sourceUrl` is kept for attribution and debugging, never fetched by the
   * browser. Null when the article had no usable image — most layouts of a
   * news feed need to handle that anyway, since roughly a third don't.
   */
  image: z
    .object({
      hash: z.string(),
      sourceUrl: z.string(),
      /** Derived on feed reads from cached lead-image bytes, never persisted. */
      color: z
        .string()
        .regex(/^#[0-9a-f]{6}$/)
        .nullable()
        .optional()
        .catch(null),
    })
    .nullable()
    .default(null),
  /** Normalized key used to deduplicate against stories already seen. */
  dedupeKey: z.string(),
  /**
   * Which **thread** this story belongs to — the set of earlier stories in the
   * same topic about the same developing subject (NEWS-280). The id of the
   * thread's first story, so a story that joined nothing carries **its own id**
   * and is a thread of one.
   *
   * Emphatically *not* `dedupeKey`, which asks a different question: that is a
   * URL identity ("is this the same article?"), this is subject identity ("is
   * this the same story unfolding?"). See `src/threads.ts` and
   * [docs/29-story-threads.md](../../docs/29-story-threads.md).
   *
   * Empty means "not recorded" — a row from a backup or a `data.json` written
   * before this existed — and the transform below reads that as a thread of one
   * rather than rejecting the row. The backfill then threads it for real.
   */
  threadId: z.string().default(''),
  foundAt: z.string(),
});

export const NewsItemSchema = NewsItemFieldsSchema.transform((item) => ({
  ...item,
  // Defaulting to the item's own id has to happen here rather than in the field:
  // "a thread of one" is defined in terms of a sibling, which a field-level
  // `.default()` cannot see. Applied on read as well as write, so nothing
  // downstream ever has to handle an empty thread id.
  threadId: item.threadId === '' ? item.id : item.threadId,
}));
export type NewsItem = z.infer<typeof NewsItemSchema>;

/**
 * The account↔subject subscription (NM-126, docs/43). One row per (account,
 * subject) the account follows; `paused` is the per-account view pause.
 */
export const SubscriptionSchema = z.object({
  accountId: z.string(),
  topicId: z.string(),
  createdAt: z.string(),
  paused: z.boolean().default(false),
});
export type Subscription = z.infer<typeof SubscriptionSchema>;

/** A per-account overlay row (`saved_items` / `hidden_items`, NM-126). */
export const ItemOverlaySchema = z.object({
  accountId: z.string(),
  itemId: z.string(),
});
export type ItemOverlay = z.infer<typeof ItemOverlaySchema>;

export const SettingsSchema = z.object({
  /**
   * Light, dark, or follow the system (FR-3.74, NEWS-334).
   *
   * **`auto` is the default**, and it is not merely "no preference expressed" —
   * it is a real third choice that keeps tracking the OS as it changes through
   * the day. Storing it explicitly is what lets someone go back to it after
   * pinning one.
   */
  theme: z.enum(['auto', 'light', 'dark']).default('auto').catch('auto'),
  /**
   * Where the user is, in their own words (NEWS-393); '' means don't localize.
   *
   * **Free text, deliberately — no place list, no validation beyond a length
   * cap.** A picker backed by a gazetteer would have to decide whether 東京 and
   * "Tokyo" are the same row, and would reject the village that isn't in it. The
   * value's whole job is to reach the check prompt as a phrase the model can
   * read, so any string a person recognises as their own place is already
   * correct. "Lisbon", "Lisboa", "北海道", "the Scottish Borders" are all fine.
   *
   * Granularity is the user's to pick: a continent is as valid an answer as a
   * street. Nothing downstream parses this into levels — the model is told where
   * the user is and left to judge how much of that matters per topic (FR-35.4).
   */
  location: z.string().default(''),
  /**
   * Reader profiles the user ticked during setup (NEWS-383), by **id**.
   *
   * Ids rather than labels so rewording a chip cannot orphan everyone who
   * ticked it — see `src/profiles.ts`. Unknown ids survive storage and are
   * dropped on read (`resolveProfiles`), which is what lets an export from a
   * build with one extra profile import without failing wholesale.
   *
   * Read by the picker (to pre-tick) and, in time, by topic suggestion —
   * NEWS-382 and NEWS-386. Persisted rather than consumed once precisely
   * because those readers exist.
   */
  profiles: z.array(z.string()).default([]).catch([]),
  /**
   * Whether to raise an OS notification (and bounce the dock / flash the
   * taskbar) when new stories arrive while the app isn't focused. Opt-in — off
   * by default, since it needs a browser permission grant and shouldn't
   * surprise anyone.
   */
  notifyOnNewItems: z.boolean().default(false),
  // Story retention was a per-account setting here until NM-169; it is now a fixed
  // window (`RETENTION_DAYS`), so there is no field. A stored `itemRetentionDays`
  // from before is simply dropped on read (zod strips unknown keys), no migration —
  // the same clean removal NM-119 made for the spend cap.
});
/**
 * A note that a database was set aside as unreadable and the app started fresh
 * (NEWS-340, [FR-4.9](../../docs/4-cli-server-storage.md)).
 *
 * Stored in `meta` rather than held in memory so it survives the restart that
 * follows — the user is most likely to read the app's explanation on the *next*
 * launch, once they have noticed their topics are missing — and so dismissing
 * it can be permanent.
 */
export const QuarantineSchema = z.object({
  /** Where the unreadable database was copied to. The only route back to it. */
  backupPath: z.string(),
  /** When it happened, ISO-8601. */
  at: z.string(),
});

export type Quarantine = z.infer<typeof QuarantineSchema>;

/**
 * Maintenance mode (NM-75) — a global operator switch, stored in the `meta`
 * table (key `maintenance`), read on every `/api/state` and every scheduler
 * sweep. `enabled` + a `message` post a banner to every signed-in reader;
 * `pauseChecks` quiets the scheduled sweep (admin force-check is unaffected).
 * `updatedBy*`/`updatedAt` are for the console's own "who set this, when" line
 * and are never sent to the reader. Every field defaults, so a missing or
 * partial row reads as "maintenance off" rather than throwing.
 */
export const MaintenanceSchema = z.object({
  enabled: z.boolean().default(false),
  message: z.string().default(''),
  pauseChecks: z.boolean().default(false),
  updatedByEmail: z.string().default(''),
  updatedAt: z.number().default(0),
});

export type Maintenance = z.infer<typeof MaintenanceSchema>;

export type Settings = z.infer<typeof SettingsSchema>;

/**
 * A registered user account (NM-20, phase 1).
 *
 * The trust boundary for a row read out of the `accounts` table, the same way
 * `TopicSchema` guards `topics`. Timestamps are ISO strings in `text` columns,
 * matching the rest of the app (topics/items/runs all keep ISO strings rather
 * than a `timestamptz` conversion layer).
 *
 * `role` and `status` are closed enums — the values the app acts on. `plan` is a
 * free-text nullable label (there is no plan machinery in this app yet; it is
 * carried from the identity for forward use). `identityIssuer` /
 * `identitySubject` are the external identity the account belongs to.
 */
export const AccountSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  role: z.enum(['member', 'admin']),
  status: z.enum(['active', 'suspended', 'disabled']),
  plan: z.string().nullable().default(null),
  identityIssuer: z.string(),
  identitySubject: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Last authenticated API use (NM-278); null = never recorded → dormant. */
  lastActiveAt: z.string().nullable().default(null),
});
export type Account = z.infer<typeof AccountSchema>;

/**
 * What a check consumed (NEWS-79). Counts only — cost is derived from these at
 * display time, so a price change can't make a stored record wrong.
 */
export const TokenUsageSchema = z.object({
  inputTokens: z.number().int().nonnegative(),
  cacheReadTokens: z.number().int().nonnegative(),
  cacheWriteTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  webSearches: z.number().int().nonnegative(),
  // Brave Search requests + a placeholder for future email sends (NM-66).
  // `.default(0)` so a pre-NM-66 stored usage (which has neither key) reads back
  // as zero rather than failing validation.
  braveSearches: z.number().int().nonnegative().default(0),
  emailsSent: z.number().int().nonnegative().default(0),
});

export const CheckRunSchema = z.object({
  id: z.string(),
  topicId: z.string(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  status: z.enum(['running', 'succeeded', 'failed']),
  newItems: z.number().int(),
  error: z.string().nullable(),
  /** Which provider ran this check (e.g. "anthropic"); null if it never resolved one. */
  provider: z.string().nullable().default(null),
  /** Model the check ran on, for pricing the usage below; null if unknown. */
  model: z.string().nullable().default(null),
  /**
   * Tokens and searches this check consumed, or null when the provider can't
   * report them (the subscription CLIs never do). Null means **unknown**, not
   * zero — spend figures must keep the two apart.
   */
  usage: TokenUsageSchema.nullable().default(null),
  /**
   * Effort the check actually ran at (NEWS-226); null for runs recorded before
   * this existed, and '' when the provider takes no such parameter.
   *
   * Null and '' are deliberately different: null is "we did not record this",
   * '' is "it ran at the model's default". Collapsing them would make every
   * historical run look like a default-effort data point.
   */
  effort: z.string().nullable().default(null),
  /**
   * The failure classification (NM-65) from `classifyFailure` — `rate-limited`
   * (the provider throttled us), `retryable` (a transient blip), or `fatal` (the
   * user's own config is broken, e.g. a bad key). Null on a success, and null on
   * a run recorded before this existed. Lets the console tell "your config is
   * broken" from "the provider is having a moment" without re-parsing English
   * error strings.
   */
  kind: z.enum(['rate-limited', 'retryable', 'fatal']).nullable().default(null),
});
export type CheckRun = z.infer<typeof CheckRunSchema>;

export const DataFileSchema = z.object({
  topics: z.array(TopicSchema),
  items: z.array(NewsItemSchema),
  settings: SettingsSchema,
  runs: z.array(CheckRunSchema),
  // The shared-model join + overlays (NM-126), so a whole-database set-aside
  // recovery restores who subscribes to what and each account's saved/hidden
  // marks. Default to empty arrays so a backup written before the shared model
  // still parses (its topics were per-account and carry no subscriptions).
  subscriptions: z.array(SubscriptionSchema).default([]),
  saved: z.array(ItemOverlaySchema).default([]),
  hidden: z.array(ItemOverlaySchema).default([]),
});
export type DataFile = z.infer<typeof DataFileSchema>;

/**
 * The `discovery_cache.payload` column: a `TopicSuggestion[]` (NM-31).
 *
 * The store validates this on read exactly as it validates every other row —
 * the same trust boundary the topic/item/run schemas draw. A row that fails is
 * treated as a **miss** (and pruned), never thrown, matching the tolerance the
 * rest of the Store applies to a row it cannot parse.
 *
 * Declared here rather than importing `TopicSuggestionSchema` from
 * `src/api/schemas.ts`, so the store keeps its rule of not depending on the API
 * layer (the same reason `ItemCursor`/`ThreadSummary` are local to the store).
 * `satisfies z.ZodType<TopicSuggestion[]>` pins it to the canonical shape in
 * `ai/types.ts`, so a drift is a type error rather than a silent divergence.
 */
export const DiscoveryCachePayloadSchema = z.array(
  z.object({
    name: z.string(),
    reason: z.string(),
    kind: z.enum(['ongoing', 'evergreen']),
    classification: z.object({ category: z.string(), subcategory: z.string().nullable() }).nullable(),
  }),
) satisfies z.ZodType<TopicSuggestion[]>;

/**
 * One row of the admin account-status audit log (NM-48).
 *
 * The store validates this on read exactly as it validates every other row — the
 * same trust boundary the topic/item/account schemas draw. A row that fails is
 * **skipped** (not thrown), matching the tolerance the rest of the Store applies
 * to a row it cannot parse; the write side (`recordAdminAction`) takes plain
 * strings, so the validation lives here on read.
 *
 * `fromStatus`/`toStatus` are the closed account-status enum: a stored value
 * outside it is a corrupt row and is dropped on read. Actor/target emails are
 * denormalized free text (an orphaned account may be gone), so they stay
 * `z.string()`. `createdAt` is epoch-ms — Drizzle reads the `bigint` column back
 * as a JS number (`mode: 'number'`).
 */
export const AdminActionSchema = z.object({
  id: z.string(),
  actorAccountId: z.string(),
  actorEmail: z.string(),
  targetAccountId: z.string(),
  targetEmail: z.string(),
  fromStatus: z.enum(['active', 'suspended', 'disabled']),
  toStatus: z.enum(['active', 'suspended', 'disabled']),
  // Role transition on a role-change action (NM-71); null on a status change. The
  // render discriminates on these being present and different.
  fromRole: z.enum(['member', 'admin']).nullable().default(null),
  toRole: z.enum(['member', 'admin']).nullable().default(null),
  // Plan transition on a plan-change action (NM-151); null otherwise. Lenient
  // strings (not a `PlanSchema` enum) so a legacy/renamed plan value in an old
  // audit row still reads — the log is a record of what happened, not live state.
  fromPlan: z.string().nullable().default(null),
  toPlan: z.string().nullable().default(null),
  // The action kind (NM-69, extended NM-106/NM-147): 'delete' on an account-deletion
  // row, 'remove_story' on a story-moderation row, 'accept_off_topic'/'reject_off_topic'
  // on an off-topic-review row, null on the status/role rows (told apart by their own
  // column pairs). A closed enum like the roles — a value outside it is a corrupt row
  // and dropped on read.
  action: z
    .enum(['delete', 'reset_account', 'remove_story', 'accept_off_topic', 'reject_off_topic'])
    .nullable()
    .default(null),
  createdAt: z.number(),
});
export type AdminAction = z.infer<typeof AdminActionSchema>;

/**
 * One row of the blocked-topics table (NM-58).
 *
 * The store validates this on read exactly as it validates every other row — the
 * same trust boundary the topic/item/account schemas draw. A row that fails is
 * **skipped** (not thrown), matching the tolerance the rest of the Store applies
 * to a row it cannot parse.
 *
 * `normalizedName` is the grouping key (`topicKey`, trimmed / lowercased /
 * whitespace-collapsed); `displayName` a human-readable form; `blockedBy` the
 * admin's email (denormalized free text, so it stays `z.string()`); `createdAt`
 * is epoch-ms — Drizzle reads the `bigint` column back as a JS number.
 */
export const BlockedTopicSchema = z.object({
  normalizedName: z.string(),
  displayName: z.string(),
  blockedBy: z.string(),
  createdAt: z.number(),
});
export type BlockedTopic = z.infer<typeof BlockedTopicSchema>;

/**
 * One row of the read-only "View as" access log (NM-56).
 *
 * Validated on read like every other row — the same trust boundary. A row that
 * fails is **skipped** (not thrown), matching the tolerance the rest of the Store
 * applies. Actor/target ids and emails are denormalized free text (an orphaned
 * account may be gone), so they stay `z.string()`; `createdAt` is epoch-ms —
 * Drizzle reads the `bigint` column back as a JS number (`mode: 'number'`).
 */
export const AccountViewSchema = z.object({
  id: z.string(),
  adminAccountId: z.string(),
  adminEmail: z.string(),
  targetAccountId: z.string(),
  targetEmail: z.string(),
  createdAt: z.number(),
});
export type AccountView = z.infer<typeof AccountViewSchema>;

/**
 * An auth/enforcement event (NM-67): a denied access or a registration. `event`
 * is the denial `code` (or `registered`); identity and account are nullable —
 * a denial may carry one, both, or neither. Validated on read like every log
 * row; a corrupt row is skipped rather than taking the log down.
 */
export const EnforcementEventSchema = z.object({
  id: z.string(),
  event: z.enum([
    'authentication_required',
    'registration_required',
    'account_suspended',
    'account_disabled',
    'admin_required',
    'registered',
    'rate_limited',
    'signup_not_invited',
  ]),
  identityIssuer: z.string().nullable().default(null),
  identitySubject: z.string().nullable().default(null),
  accountId: z.string().nullable().default(null),
  accountEmail: z.string().nullable().default(null),
  path: z.string(),
  method: z.string(),
  createdAt: z.number(),
});
export type EnforcementEvent = z.infer<typeof EnforcementEventSchema>;
export type EnforcementEventName = EnforcementEvent['event'];

/**
 * One sign-up allow-list entry (NM-260) — a permitted email plus who added it and
 * when, for the admin console. Validated on read like every stored row; a corrupt
 * row is skipped rather than taking the list (and thus registration) down.
 */
export const SignupAllowlistEntrySchema = z.object({
  email: z.string(),
  addedByEmail: z.string().nullable().default(null),
  createdAt: z.number(),
});
export type SignupAllowlistEntry = z.infer<typeof SignupAllowlistEntrySchema>;

/**
 * The outcome of one housekeeping prune (NM-73), persisted to the `meta` key
 * `last-prune` so the data-health panel can show when housekeeping last ran and
 * how much it reclaimed — otherwise a wedged or never-running sweep is invisible.
 * `at` is an ISO-8601 UTC string; the counts are what that pass removed. Validated
 * on read like every stored blob; a corrupt value reads as "no prune recorded".
 */
export const PruneResultSchema = z.object({
  at: z.string(),
  storiesPruned: z.number(),
  runsPruned: z.number(),
  orphanItems: z.number(),
  orphanRuns: z.number(),
});
export type PruneResult = z.infer<typeof PruneResultSchema>;

/**
 * One per-subsystem health sample (NM-68). `subsystem` and `status` are closed
 * enums (a value outside them is a corrupt row, dropped on read like every other
 * log); `detail` is an optional human note (a probe error, a missing key); `at`
 * is epoch-ms — Drizzle reads the `bigint` column back as a JS number.
 */
export const HealthSampleSchema = z.object({
  id: z.string(),
  subsystem: z.enum(['database', 'provider', 'search', 'scheduler']),
  status: z.enum(['ok', 'degraded', 'down']),
  detail: z.string().nullable().default(null),
  at: z.number(),
});
export type HealthSample = z.infer<typeof HealthSampleSchema>;
export type HealthSubsystem = HealthSample['subsystem'];
export type HealthStatus = HealthSample['status'];

/**
 * One transactional-email delivery attempt (NM-116). `event` is which
 * account-lifecycle mail; `status` is `sent` / `failed` / `skipped` (skipped =
 * no transport configured — the dev/no-op case); `transport` names the mailer
 * that handled it; `error` carries the failure detail when any. Validated on
 * read like every log row — a corrupt row is dropped rather than taking the log
 * down. `createdAt` is epoch-ms (Drizzle reads the bigint back as a JS number).
 */
export const EmailDeliverySchema = z.object({
  id: z.string(),
  event: z.enum(['registration', 'suspension', 'disable', 'deletion']),
  recipient: z.string(),
  subject: z.string(),
  status: z.enum(['sent', 'failed', 'skipped']),
  transport: z.string(),
  error: z.string().nullable().default(null),
  createdAt: z.number(),
});
export type EmailDelivery = z.infer<typeof EmailDeliverySchema>;
export type EmailEvent = EmailDelivery['event'];
export type EmailStatus = EmailDelivery['status'];

export function emptyDataFile(): DataFile {
  return {
    topics: [],
    items: [],
    settings: {
      location: '',
      profiles: [],
      notifyOnNewItems: false,
      theme: 'auto',
    },
    runs: [],
    subscriptions: [],
    saved: [],
    hidden: [],
  };
}
